import FormLabel from '@mui/material/FormLabel';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem, { MenuItemProps } from '@mui/material/MenuItem';
import MuiTextField, {
  TextFieldProps as MuiTextFieldProps,
} from '@mui/material/TextField';
import { MouseEventHandler } from 'react';

import { Box } from './Box';

interface SelectOptionProps extends MenuItemProps {
  label: string | React.ReactNode;
  value: string | number | undefined;
  clearable?: boolean;
}

interface TextFieldProps {
  value?: MuiTextFieldProps['value'];
  onBlur?: MuiTextFieldProps['onBlur'];
  onChange?: MuiTextFieldProps['onChange'];
  onFocus?: MuiTextFieldProps['onFocus'];
  onKeyDown?: MuiTextFieldProps['onKeyDown'];
  onClick?: MouseEventHandler<HTMLElement>;
  type?: MuiTextFieldProps['type'];
  minRows?: MuiTextFieldProps['minRows'];
  maxRows?: MuiTextFieldProps['maxRows'];
  disabled?: boolean | 'click-only';
  autoComplete?: MuiTextFieldProps['autoComplete'];
  error?: MuiTextFieldProps['error'];
  helperText?: MuiTextFieldProps['helperText'];
  id?: MuiTextFieldProps['id'];
  multiline?: MuiTextFieldProps['multiline'];
  placeholder?: MuiTextFieldProps['placeholder'];
  label?: MuiTextFieldProps['label'];
  size?: MuiTextFieldProps['size'];
  rows?: MuiTextFieldProps['rows'];
  sx?: MuiTextFieldProps['sx'];
  variant?: 'outlined' | 'standard';
  fullWidth?: boolean;
  required?: boolean;
  autoFocus?: boolean;
  startAdornment?: React.ReactNode;
  endAdornment?: React.ReactNode;
  InputProps?: MuiTextFieldProps['InputProps'];
  options?: SelectOptionProps[];
  customOptions?: React.ReactNode;
  slotProps?: MuiTextFieldProps['slotProps'];
  resize?: 'none' | 'both' | 'horizontal' | 'vertical';
}

/**
 * This component is a thin wrapper around MuiTextField.
 * 1. Removes the default floating label behavior and adds a static label (Can be re-enabled with floatingLabel prop)
 * 2. Allows setting startAdornment and endAdornment directly without InputProps
 * 3. Disallows filled variant
 */
export const TextField: React.FC<TextFieldProps> = ({ ...props }) => {
  const {
    label,
    startAdornment,
    endAdornment,
    options,
    customOptions,
    size,
    sx,
    multiline,
    disabled,
    onClick,
    fullWidth = true,
    resize = 'vertical',
    ...otherProps
  } = props;

  const isSelect = !!options || !!customOptions;

  return (
    <FormLabel
      sx={[
        { display: 'block', position: 'relative' },
        fullWidth && { width: '100%' },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <MuiTextField
        {...otherProps}
        label={label}
        fullWidth={fullWidth}
        multiline={multiline}
        disabled={!!disabled}
        size={size}
        select={isSelect}
        slotProps={{
          input: {
            startAdornment,
            endAdornment,
            sx: [
              (theme) => ({
                '& .MuiInputBase-input.Mui-disabled': {
                  WebkitTextFillColor:
                    disabled === 'click-only'
                      ? theme.vars.palette.text.primary
                      : theme.vars.palette.text.disabled,
                },
              }),
              !!multiline && resize
                ? {
                    '& .MuiInputBase-input': {
                      resize: resize,
                    },
                  }
                : !!multiline && { py: 1 },
            ],
            ...props.InputProps,
          },
          ...props.slotProps,
        }}
      >
        {isSelect &&
          (customOptions
            ? customOptions
            : options?.map((option) => (
                <MenuItem
                  {...option}
                  key={option.value}
                  value={option.value}
                  sx={{ my: 0.25, mx: 1 }}
                >
                  {option.label}
                </MenuItem>
              )))}
      </MuiTextField>

      {disabled === 'click-only' && (
        <Box
          onClick={onClick}
          tabIndex={0}
          role="button"
          component="button"
          sx={{
            width: '100%',
            height: '100%',
            position: 'absolute',
            left: '0px',
            top: '0px',
            bgcolor: 'transparent',
            borderColor: 'transparent',
            cursor: 'pointer',
            '&:hover': {
              bgcolor: 'transparent',
              borderColor: 'transparent',
            },
          }}
        ></Box>
      )}
    </FormLabel>
  );
};

export { InputAdornment };
export type { TextFieldProps };
